import React, { Fragment, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  createPosPaymentRequest,
  getCashTypeForShift,
  isPosTerminalPayment,
  printOrder,
  posTerminalRequest,
  isCloudPosPaymentRequest,
  isPcPosPaymentRequest,
  createPcPosPaymentRequest
} from "utils"
import { Button, PcPosTerminalPaymentStatusModal, PosTerminalPaymentStatusModal } from "components"
import { approvePayment, getOrder } from "services"
import { addNewShiftOrder, showWarningTopup } from "@redux"
import classes from "./CheckoutSplitButton.module.scss"

const CheckoutSplitButton = props => {
  const {
    total,
    cashTypes,
    totalTax,
    totalCharges,
    totalDiscount,
    onComplete,
    orderItems,
    orderCombos,
    selectedUserAccount,
    printCustomerReceipt,
    sendPrintCustomerReceiptToPos
  } = props

  const [t] = useTranslation()

  const history = useHistory()

  const dispatch = useDispatch()

  const orderForEditDetails =
    useSelector(state =>
      state.orderForEditDetails
    )

  const tableNumber =
    useSelector(state =>
      state.order.tableNumber
    )

  const deliveryType =
    useSelector(state =>
      state.deliveryInfo.deliveryType
    )

  const temporaryOff =
    useSelector(state =>
      state.systemSettings.temporaryOff
    )

  const posTerminals =
    useSelector(state => state.posTerminals)

  const cashierAccountId =
    useSelector(state => state.userAccountInfo._id)

  const [sending, setSending] = useState(false)
  const [posTerminalPaymentStatus, setPosTerminalPaymentStatus] = useState("")
  const [pcPosTerminalPaymentStatus, setPcPosTerminalPaymentStatus] = useState("")
  const [openPcPosTerminalPaymentStatusModal, setOpenPcPosTerminalPaymentStatusModal] = useState(false)
  const [openPosTerminalPaymentStatusModal, setOpenPosTerminalPaymentStatusModal] = useState(false)

  const unmount = useRef(false)

  const handlePosTerminalRequestSent = () => {
    if (unmount.current) return

    setSending(false)

    setPosTerminalPaymentStatus("requestSent")
  }

  const handlePosTerminalRequestAcknowledge = () => {
    if (unmount.current) return

    setSending(false)

    setPosTerminalPaymentStatus("acknowledge")
  }

  const handlePosTerminalPaymentSuccess = () => {
    onComplete()

    history.push("/foods")
  }

  const newShiftOrder = (shiftOrder) => {
    dispatch(
      addNewShiftOrder(shiftOrder)
    )
  }

  const printRequest = async () => {
    if (!printCustomerReceipt) return

    const order =
      await getOrder(orderForEditDetails.orderId)

    if (order.orderStatus === "pending") return

    const skipKitchenReceipt = true

    const skipOrderItems = true

    printOrder(
      order,
      skipKitchenReceipt,
      skipOrderItems
    )
  }

  const validate = () => {
    let result = true

    const noTableNumber =
      deliveryType === "dineIn" && !tableNumber

    if (noTableNumber) {
      toast.error(t("لطفا_میز_را_وارد_کنید"))

      return false
    }

    if (cashTypes.length === 0) {
      toast.error(t("لطفا_بک_روش_پرداخت_انتخاب_کنید"))

      result = false
    }

    return result
  }

  const getSubPaymentItemIds = () => {
    const subPaymentItemIds = []

    const { subPaymentItems } = orderForEditDetails

    orderItems.map(o =>
      subPaymentItemIds.push(
        subPaymentItems[o.orderItemId].subPaymentItemId
      )
    )

    orderCombos.map(o =>
      o.items.map(comboItem =>
        subPaymentItemIds.push(
          subPaymentItems[comboItem.orderItemId].subPaymentItemId
        )
      )
    )

    return subPaymentItemIds
  }

  const handleSplit = async () => {
    if (!validate()) return

    setSending(true)

    const subPaymentItemIds =
      getSubPaymentItemIds()

    const cashType =
      getCashTypeForShift(cashTypes, total)

    const shiftOrder = {
      taxes: totalTax,
      charges: totalCharges,
      discounts: totalDiscount,
      cashTypes: cashType
    }

    const posTerminalName =
      cashTypes[0].terminalName

    const posPayment =
      isPosTerminalPayment(cashTypes)

    const cloudPosPaymentRequest =
      isCloudPosPaymentRequest(cashTypes)

    const pcPosPaymentRequest =
      isPcPosPaymentRequest(cashTypes)

    try {
      if (pcPosPaymentRequest) {
        const posTerminalDetails =
          posTerminals[cashierAccountId]?.find(t =>
            t.terminalName === posTerminalName
          )

        if (!posTerminalDetails) return setSending(false)

        setPcPosTerminalPaymentStatus("loading")

        setOpenPcPosTerminalPaymentStatusModal(true)

        const result =
          await createPcPosPaymentRequest(
            total,
            posTerminalDetails.ipAddress,
            posTerminalDetails.comPort,
            posTerminalDetails.posPort
          )

        if (unmount.current) return

        if (result.status !== "Success") {
          setSending(false)

          setPcPosTerminalPaymentStatus("failed")

          return
        }

        setPcPosTerminalPaymentStatus("success")
      }

      if (cloudPosPaymentRequest) {
        setOpenPosTerminalPaymentStatusModal(true)

        const posPaymentResult =
          await posTerminalRequest.send(
            total,
            posTerminalName,
            handlePosTerminalRequestSent,
            handlePosTerminalRequestAcknowledge
          )

        if (unmount.current) return

        if (posPaymentResult === "canceled") return

        if (posPaymentResult === "unsuccess") {
          return setPosTerminalPaymentStatus("paymentFailed")
        }

        if (posPaymentResult === "requestFailed") {
          return setPosTerminalPaymentStatus("failedRequest")
        }
      }

      if (posPayment) {
        const posPaymentResult =
          await createPosPaymentRequest(total)

        if (!posPaymentResult) {
          dispatch(
            showWarningTopup("پرداخت انجام نشد")
          )

          if (unmount.current) return

          setSending(false)

          return
        }
      }

      setSending(true)

      const query =
        !!orderForEditDetails.isGuestOrder
          ? { orderId: orderForEditDetails.orderId }
          : { accountId: selectedUserAccount?._id }

      await approvePayment(
        {
          cashType,
          subPaymentItemIds,
          posTerminalName,
          sendPrintCustomerReceiptToPos
        },
        query
      )

      newShiftOrder(shiftOrder)

      printRequest()

      if (cloudPosPaymentRequest) return

      onComplete()

      history.push("/foods")

      dispatch(
        showWarningTopup(t("پرداخت_با_موفقیت_انجام_شد"))
      )
    } catch (err) {
      console.log(err)

      dispatch(
        showWarningTopup(t("پرداخت_انجام_نشد"))
      )

      setSending(false)
    }
  }

  useEffect(() => {
    unmount.current = false

    return () => { unmount.current = true }
  }, [])

  const sumOfPayments =
    cashTypes.reduce((t, cashType) =>
      t += Number(cashType.amount)
      , 0
    )

  const change = total - sumOfPayments

  const disabled =
    change > 0 || temporaryOff

  return (
    <Fragment>
      <div className={classes["container"]}>
        <Button
          color="success"
          disabled={disabled}
          loading={sending}
          className={classes["button"]}
          onClick={handleSplit}
        >
          {temporaryOff ? t("فروشگاه_بسته_است") : t("پرداخت")}
        </Button>
      </div>

      <PosTerminalPaymentStatusModal
        onPaymentSuccess={handlePosTerminalPaymentSuccess}
        posTerminalPaymentStatus={posTerminalPaymentStatus}
        setPosTerminalPaymentStatus={setPosTerminalPaymentStatus}
        openPosTerminalPaymentStatusModal={openPosTerminalPaymentStatusModal}
        setOpenPosTerminalPaymentStatusModal={setOpenPosTerminalPaymentStatusModal}
      />


      <PcPosTerminalPaymentStatusModal
        pcPosTerminalPaymentStatus={pcPosTerminalPaymentStatus}
        openPcPosTerminalPaymentStatusModal={openPcPosTerminalPaymentStatusModal}
        setOpenPcPosTerminalPaymentStatusModal={setOpenPcPosTerminalPaymentStatusModal}
      />
    </Fragment>
  )
}

export default React.memo(CheckoutSplitButton)